/** JSON-LD builders shared across spec pages. */

export const SITE_ORIGIN = "https://autospecdata.com";

/** Single source of truth for the social share image used by every route head. */
export const SITE_OG_IMAGE = `${SITE_ORIGIN}/og-image.jpg`;

const BRAND_SUFFIX = " | AutoSpecData";

/**
 * Auditors flag titles whose rendered width exceeds the SERP line (~60
 * characters). Keep the brand suffix when the whole title still fits, drop it
 * when the data-bearing base already fills the line, and only trim as a last
 * resort. The base never equals the on-page H1 (H1s carry no year or figure),
 * so dropping the suffix cannot create a title/H1 duplicate.
 */
const MAX_TITLE = 60;

export function pageTitle(base: string): string {
  const clean = base.trim();
  if (clean.length + BRAND_SUFFIX.length <= MAX_TITLE) return clean + BRAND_SUFFIX;
  if (clean.length <= MAX_TITLE) return clean;
  return clean
    .slice(0, MAX_TITLE)
    .replace(/\s+\S*$/, "")
    .replace(/[\s:,&–—-]+$/, "");
}

/**
 * Google requires absolute URLs in structured-data `id`/`url` fields
 * (BreadcrumbList itemListElement.item, ItemList item URLs). Relative
 * paths trigger "Invalid URL in field id".
 */
export function absUrl(path: string) {
  if (!path) return SITE_ORIGIN + "/";
  if (/^https?:\/\//i.test(path)) return path;
  return SITE_ORIGIN + (path.startsWith("/") ? path : `/${path}`);
}

export interface Crumb {
  name: string;
  path: string;
}

export function breadcrumbLd(crumbs: Crumb[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: absUrl(c.path),
    })),
  };
}

/**
 * ItemList for directory and ranking pages.
 *
 * Only the first `max` entries are serialised: search engines read an ItemList
 * as a sample of the page, and emitting 500+ ListItems added tens of KB of
 * non-rendered JSON to the document. `numberOfItems` still reports the true
 * total so the markup stays honest about the size of the list.
 */
export function itemListLd(name: string, items: { name: string; path: string }[], max = 50) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    numberOfItems: items.length,
    itemListElement: items.slice(0, max).map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      url: absUrl(it.path),
    })),
  };
}

/**
 * Marks the on-page BLUF answer as the passage voice/answer engines should read
 * aloud or lift verbatim. Selectors must match elements that exist in the SSR
 * HTML — `[data-aeo="atomic-passage"]` is the BlufSummary container.
 */
export function speakableLd(url: string, selectors: string[] = ['[data-aeo="atomic-passage"]']) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: selectors,
    },
  };
}

/**
 * Vehicle-page structured data.
 *
 * NOTE: schema.org `Vehicle` is a subtype of `Product`, so Google validates it
 * as a merchant listing and reports "Either offers, review, or aggregateRating
 * should be specified". These are reference spec pages, not product listings,
 * so we emit a `Dataset` describing the vehicle's specs instead — no Product
 * validation, and it matches the Dataset enhancements already reported valid.
 */
export function vehicleLd(opts: {
  make: string;
  model: string;
  path: string;
  yearMin?: number | null;
  yearMax?: number | null;
  bestMpg?: number | null;
  fuelType?: string | null;
  vehicleClass?: string | null;
  transmission?: string | null;
  drive?: string | null;
  description: string;
}) {
  const keywords = [opts.make, opts.model, opts.fuelType, opts.vehicleClass, opts.transmission, opts.drive].filter(
    Boolean,
  ) as string[];

  const ld: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    // Page-scoped @id keeps this distinct from the sitewide database Dataset
    // declared in the root graph.
    "@id": `${absUrl(opts.path)}#dataset`,
    name: `${opts.make} ${opts.model} specifications`,
    url: absUrl(opts.path),
    description: opts.description,
    keywords,
    license: "https://creativecommons.org/licenses/by/4.0/",
    isAccessibleForFree: true,
    creator: {
      "@type": "Organization",
      "@id": "https://autospecdata.com/#organization",
      name: "AutoSpecData",
      url: "https://autospecdata.com",
    },
    isPartOf: { "@id": "https://autospecdata.com/#dataset" },
    about: {
      "@type": "Thing",
      name: `${opts.make} ${opts.model}`,
    },
  };
  if (opts.yearMin && opts.yearMax) {
    ld["temporalCoverage"] = `${opts.yearMin}/${opts.yearMax}`;
  } else if (opts.yearMin) {
    ld["temporalCoverage"] = String(opts.yearMin);
  }
  if (opts.bestMpg) {
    ld["variableMeasured"] = [
      {
        "@type": "PropertyValue",
        name: "Best combined fuel economy",
        value: opts.bestMpg,
        unitText: "MPG",
      },
    ];
  }
  const vehicle: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Vehicle",
    "@id": `${absUrl(opts.path)}#vehicle`,
    name: `${opts.make} ${opts.model}`,
    brand: { "@type": "Brand", name: opts.make },
    model: opts.model,
    ...(opts.yearMax ? { modelDate: String(opts.yearMax) } : {}),
    ...(opts.fuelType ? { fuelType: opts.fuelType } : {}),
    ...(opts.vehicleClass ? { bodyType: opts.vehicleClass } : {}),
  };
  return [ld, vehicle];
}

export function faqLd(items: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/**
 * Dataset node for tabular pages (rankings, hubs, comparisons).
 *
 * Same shape spec/attribute pages already emit via vehicleLd, so every genuinely
 * tabular template now declares the same Dataset contract: page-scoped @id,
 * Organization creator, and a link back to the sitewide database Dataset.
 */
export function tableDatasetLd(opts: {
  name: string;
  path: string;
  description: string;
  keywords?: string[];
  rowCount?: number | null;
  yearMin?: number | null;
  yearMax?: number | null;
  measures?: { name: string; unitText?: string; value?: number }[];
}) {
  const url = absUrl(opts.path);
  const ld: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "@id": `${url}#dataset`,
    name: opts.name,
    url,
    description: opts.description,
    license: "https://creativecommons.org/licenses/by/4.0/",
    isAccessibleForFree: true,
    creator: {
      "@type": "Organization",
      "@id": `${SITE_ORIGIN}/#organization`,
      name: "AutoSpecData",
      url: SITE_ORIGIN,
    },
    isPartOf: { "@id": `${SITE_ORIGIN}/#dataset` },
  };
  if (opts.keywords && opts.keywords.length > 0) ld["keywords"] = opts.keywords;
  if (opts.rowCount && opts.rowCount > 0) {
    ld["distribution"] = [{ "@type": "DataDownload", encodingFormat: "text/html", contentUrl: url }];
    ld["size"] = `${opts.rowCount} rows`;
  }
  if (opts.yearMin && opts.yearMax) ld["temporalCoverage"] = `${opts.yearMin}/${opts.yearMax}`;
  else if (opts.yearMin) ld["temporalCoverage"] = String(opts.yearMin);
  if (opts.measures && opts.measures.length > 0) {
    ld["variableMeasured"] = opts.measures.map((m) => ({
      "@type": "PropertyValue",
      name: m.name,
      ...(m.unitText ? { unitText: m.unitText } : {}),
      ...(typeof m.value === "number" ? { value: m.value } : {}),
    }));
  }
  return ld;
}

/**
 * Calculator/tool pages.
 *
 * Previously these emitted `SoftwareApplication`, which Google validates as a
 * merchant-style entity and flags with "Either offers, review, or
 * aggregateRating should be specified". These are free client-side utilities
 * with no real ratings, so we emit a `WebPage` instead and hang the
 * `speakable` spec off it (speakable is only valid on WebPage-type nodes).
 */
export function toolPageLd(opts: { name: string; path: string; description: string; speakableSelectors?: string[] }) {
  const url = absUrl(opts.path);
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    name: opts.name,
    url,
    description: opts.description,
    isAccessibleForFree: true,
    inLanguage: "en-US",
    publisher: {
      "@type": "Organization",
      "@id": `${SITE_ORIGIN}/#organization`,
      name: "AutoSpecData",
      url: SITE_ORIGIN,
    },
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: opts.speakableSelectors ?? ["h1", "[data-quick-answer]"],
    },
  };
}
